import { type ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useDebounce } from '@/hooks/ui/use-debounce'
import type { DesiredEducationCourse } from '@/types/education/desired-education-course'
import type { StudentRequestedCoursesFormMethods } from '@/types/forms/student-requested-courses-form-methods'
import type { TableData } from '@/types/table/table-data'
import { createTableConfig } from '@/utils/create-table-config'

export const useChooseCourses = (
  availableCollegeCourses?: DesiredEducationCourse[],
  formMethods?: StudentRequestedCoursesFormMethods,
  variant?: 'manager' | 'student'
) => {
  const [isOpen, setOpen] = useState(false)
  const [localSelectedCollegeCourses, setLocalSelectedCollegeCourses] =
    useState<DesiredEducationCourse[]>([])
  const [localCollegeCourses, setLocalCollegeCourses] = useState<
    DesiredEducationCourse[]
  >(availableCollegeCourses || [])
  const [searchText, setSearchText] = useState('')
  const searchDebounce = useDebounce(searchText, 500)

  // ⬇️ Memoizar cursos disponíveis para evitar ref nova a cada render
  const stableAvailableCourses = useMemo(
    () => availableCollegeCourses ?? [],
    [JSON.stringify(availableCollegeCourses)] // só muda se o conteúdo mudar
  )

  const tableConfig = useMemo(
    () => createTable(localCollegeCourses),
    [localCollegeCourses]
  )

  const handleSearchInput = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSubmit = () => {
    localSelectedCollegeCourses.forEach((course) => {
      formMethods?.append?.({
        equivalenceStatus: variant === 'manager' ? { id: '' } : undefined,
        desiredCourse: {
          id: course.id ?? '',
          courseId: course.courseId,
          name: course.name,
          workload: course.workload,
          period: course.period,
          serie: String(course.serie)
        },
        previousCourse: {
          id: '',
          finalNumericGrade: '',
          name: '',
          period: '',
          workload: 0
        },
        selectedByAI: false
      })
    })
    setOpen(false)
    setLocalSelectedCollegeCourses([])
  }

  function formatWorkload(workload: number) {
    return `${workload}h`
  }

  function createTable(data: DesiredEducationCourse[] | undefined) {
    const tableData: TableData<DesiredEducationCourse>[] =
      data?.map((previousCourse) => ({
        data: { ...previousCourse }
      })) ?? []

    return createTableConfig<DesiredEducationCourse>(
      [
        { key: 'name', label: 'Nome da Disciplina', hidden: false },
        {
          key: 'workload',
          label: 'Carga Horária',
          hidden: false,
          width: '200px',
          toText: formatWorkload
        }
      ],
      tableData,
      'courseId'
    )
  }

  const filterBySearchText = () => {
    const courses = stableAvailableCourses.filter((course) =>
      (course.name ?? '').toLowerCase().includes(searchText.toLowerCase())
    )
    setLocalCollegeCourses(courses)
  }

  useEffect(() => {
    filterBySearchText()
  }, [searchDebounce])

  useEffect(() => {
    if (
      JSON.stringify(localCollegeCourses)
      !== JSON.stringify(stableAvailableCourses)
    ) {
      setLocalCollegeCourses(stableAvailableCourses)
    }
  }, [stableAvailableCourses])

  return {
    states: {
      isOpen,
      searchText,
      tableConfig,
      localSelectedCollegeCourses
    },
    actions: {
      setOpen,
      handleSearchInput,
      setLocalCollegeCourses,
      setLocalSelectedCollegeCourses,
      handleSubmit
    }
  }
}
